import { LearnMoreLink, Page } from '@ui/themed';
import { t } from 'i18next';
import { useMemo } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { ShippingMethodFormContext, useShippingFormLogic } from '../builder-context';
import { ShippingMethodBuilder } from '../components/shipping-method-builder';
import { useShippingMethod } from '../hooks/use-shipping-method';
import { useUpdateShippingMethod } from '../hooks/use-update-shipping-method';
import type { ShippingMethodBuilderValidated } from '../schemas';
import { transformShippingMethodToApi, transformShippingMethodToState } from '../transformers';

export const ShippingMethodEditPage = withPageProviders(ShippingMethodEditPageContent);

function ShippingMethodEditPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const updateMethod = useUpdateShippingMethod();
  const env = useEnv();
  const { id } = navigationHandler.params();

  const { data: shippingMethod, error, isLoading, refetch } = useShippingMethod(id ?? '');
  const initialState = useMemo(
    () => (shippingMethod ? transformShippingMethodToState(shippingMethod) : undefined),
    [shippingMethod],
  );

  const shippingForm = useShippingFormLogic({
    onSubmit: handleSubmit,
    initialState: initialState,
    onError: () => {
      notificationHandler.error();
    },
  });

  if (featureRestricted(features.SHIPPING, getActiveBillingPlanKey(env.billing.plan_key))) {
    navigationHandler.navigate('/configuration/shipping');
    return null;
  }

  if (!id) {
    navigationHandler.navigate('/configuration/shipping/');
    return null;
  }

  async function handleSubmit(data: ShippingMethodBuilderValidated) {
    if (!id) {
      return;
    }

    const payload = transformShippingMethodToApi(data);
    await updateMethod.mutateAsync(
      { id, payload },
      {
        onError: () => {
          notificationHandler.error();
        },
        onSuccess: () => {
          notificationHandler.success(
            t(
              'features:shipping_methods.components.shipping_method_builder.notifications.success.update_successful',
              {
                name: data.name,
              },
            ),
          );

          navigationHandler.navigate('/configuration/shipping');
        },
      },
    );
  }

  return (
    <Page
      narrow
      title={
        initialState?.name ||
        t('features:shipping_methods.components.shipping_method_builder.header.title.edit')
      }
      backUrl="/configuration/shipping"
      description={
        <>
          {t('features:shipping_methods.components.shipping_method_builder.header.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/shipping#XsUtC" />
        </>
      }
    >
      <SparkSuspense
        isError={!!error}
        isLoading={isLoading || !initialState}
        errorMessage={t('errors:generic')}
        onReload={() => void refetch()}
      >
        <ShippingMethodFormContext.Provider value={shippingForm}>
          <ShippingMethodBuilder
            isEditing
            onCancel={() => navigationHandler.navigate('/configuration/shipping')}
          />
        </ShippingMethodFormContext.Provider>
      </SparkSuspense>
    </Page>
  );
}
